import { z } from 'zod';
import { LOCALE_MODAL_PATHS } from 'types/locales';

export const ConsentProfileSchema = z.enum(['standard', 'tcpa', 'tcpa-zoocasa', 'tcpa-in-service']);
export type ConsentProfile = z.infer<typeof ConsentProfileSchema>;

// 'standard' is non-compliant for TCPA purposes — a baseline must be a compliance
// profile ('tcpa', 'tcpa-in-service') when any per-country override relaxes to standard.
const NON_COMPLIANCE_PROFILES = new Set<ConsentProfile>(['standard']);

export const ConsentRuleSchema = z.object({
  when:            z.enum(['always', 'never', 'except']),
  exemptCountries: z.array(z.string()),
  profiles:        z.record(z.string(), ConsentProfileSchema),
  baseline:        ConsentProfileSchema,
}).refine(
  rule => !(
    Object.values(rule.profiles).some(p => NON_COMPLIANCE_PROFILES.has(p)) &&
    NON_COMPLIANCE_PROFILES.has(rule.baseline)
  ),
  {
    message: "baseline must be a compliance profile ('tcpa', 'tcpa-in-service') when any country override relaxes to a non-compliance profile",
    path: ['baseline'],
  }
);
export type ConsentRule = z.infer<typeof ConsentRuleSchema>;

export type ConsentResolution = {
  required: boolean;
  profile:  ConsentProfile;
  labels:   { privacy: string; marketing: string };
};

export const CONSENT_LABELS: Record<ConsentProfile, { privacy: string; marketing: string }> = {
  standard: {
    privacy:   LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_PRIVACY_POLICY_DEFAULT,
    marketing: LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_MARKETING_DEFAULT,
  },
  tcpa: {
    privacy:   LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_PRIVACY_POLICY_EXP_OUTSIDE_CANADA,
    marketing: LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_MARKETING_EXP_OUTSIDE_CANADA,
  },
  'tcpa-zoocasa': {
    privacy:   LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_PRIVACY_POLICY_EXP_OUTSIDE_CANADA,
    marketing: LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_MARKETING_ZOOCASA_OUTSIDE_CANADA,
  },
  'tcpa-in-service': {
    privacy:   LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_PRIVACY_POLICY_EXP_OUTSIDE_CANADA,
    marketing: LOCALE_MODAL_PATHS.LOGIN_REGISTRATION_CONSENT_MARKETING_EXP_IN_SERVICE,
  },
};

// Accepts both ConsentRule and ReadonlyObjectDeep<ConsentRule> (type-fest deep-readonly from tenant config).
export type ConsentRuleInput = {
  when:            ConsentRule['when'];
  exemptCountries: readonly string[];
  profiles:        Readonly<Record<string, ConsentProfile>>;
  baseline:        ConsentProfile;
};

export function evaluateConsent(rule: ConsentRuleInput, countryCode: string | null): ConsentResolution {
  const required =
    rule.when === 'always' ||
    (rule.when === 'except' && !rule.exemptCountries.includes(countryCode ?? ''));

  const profileOverride = countryCode !== null ? rule.profiles[countryCode] : undefined;
  const profile = required ? (profileOverride ?? rule.baseline) : rule.baseline;

  const resolution: ConsentResolution = { required, profile, labels: CONSENT_LABELS[profile] };

  if (process.env.NODE_ENV === 'development') {
    console.debug(
      '%c[consent]%c evaluateConsent',
      'color:#6366f1;font-weight:bold', 'color:inherit',
      { rule: `when=${rule.when} baseline=${rule.baseline}`, countryCode, ...resolution, labels: undefined }
    );
  }

  return resolution;
}
